import React, { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { GlassmorphismContainer } from "../styled/shared/Glassmorphism";
import { useAreaGenerateStore, useCesiumStore } from "../../Store/store";
import {
  AreaGenerateIcon,
  PathGenerateIcon,
} from "../../assets/icons/WorkTypeIcons";
import { GetPolygonModeId } from "../../api/cesiumAPI";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled(GlassmorphismContainer)`
  width: 380px;
  min-height: 250px;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 20px;
  padding: 24px 28px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  text-align: center;
  color: #fff;
  margin-bottom: 20px;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Label = styled.label`
  font-size: 15px;
  font-weight: 500;
  color: #fff;
  margin-bottom: 5px;
`;

const Input = styled.input`
  height: 42px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 0 12px;
  outline: none;
  transition: all 0.2s ease;
  font-size: 14px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }

  &:focus {
    border-color: #4caf50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.25);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 10px;
`;

const Button = styled.button`
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;

  &.primary {
    background: #4caf50;
    color: white;

    &:hover {
      background: #45a049;
      transform: translateY(-1px);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      color: white;
    }
  }
`;

export default function AreaGenerateModal() {
  const {
    isAreaGenerateOpen,
    setIsAreaGenerateOpen,
    selectedWorkType,
    setSelectedWorkType,
    selectedAreaType,
    setSelectedAreaType,
    newAreaName,
    setNewAreaName,
    setName,
    Name,
    areaColor,
    setAreaColor,
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
  } = useAreaGenerateStore();

  const inputRef = useRef(null);

  const handleConfirm = useCallback(async () => {
    const data = {
      workType: selectedWorkType,
      areaType: selectedAreaType,
      Name,
      newAreaName: newAreaName,
    };

    setIsAreaGenerateOpen(false);
    if (onConfirm) {
      await onConfirm(data);
    }
  }, [
    onConfirm,
    selectedWorkType,
    selectedAreaType,
    Name,
    newAreaName,
    setIsAreaGenerateOpen,
  ]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    setIsAreaGenerateOpen(false);
  }, [onCancel, setIsAreaGenerateOpen]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        handleCancel();
      }
    },
    [handleCancel],
  );

  useEffect(() => {
    if (!isAreaGenerateOpen) return;
    const handler = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        handleConfirm();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleCancel();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isAreaGenerateOpen, handleConfirm, handleCancel]);

  useEffect(() => {
    if (isAreaGenerateOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isAreaGenerateOpen]);

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <Title>
          {selectedWorkType === "pathGenerate" ? "경로 생성" : "영역 생성"}
        </Title>

        <FormContainer>
          {/* 경로 생성시 */}
          <>
            <Label
              htmlFor={selectedWorkType ? "pathNameInput" : "areaNameInput"}
            >
              {selectedWorkType === "pathGenerate" ? "경로 이름" : "영역 이름"}
            </Label>
            <Input
              ref={inputRef}
              id={selectedWorkType ? "pathNameInput" : "areaNameInput"}
              type="text"
              placeholder={
                selectedWorkType === "pathGenerate"
                  ? "진행 경로 1번"
                  : "익산시 1번 논밭"
              }
              value={Name}
              onChange={(e) => setName(e.target.value)}
            />
          </>
        </FormContainer>

        <ButtonRow>
          <Button className="secondary" onClick={handleCancel}>
            {cancelText}
          </Button>
          <Button className="primary" onClick={handleConfirm}>
            {confirmText}
          </Button>
        </ButtonRow>
      </ModalContainer>
    </ModalOverlay>
  );
}
